function clamp (value, min, max) {
  if (max < min) {
    return min
  }
  return Math.min(Math.max(value, min), max)
}

function normalizeViewport (viewport = {}) {
  return {
    width: Number(viewport.width) || 0,
    height: Number(viewport.height) || 0
  }
}

function normalizeSize (tooltipSize = {}) {
  return {
    width: Number(tooltipSize.width) || 0,
    height: Number(tooltipSize.height) || 0
  }
}

function normalizeMovement (movement = {}) {
  return {
    x: Number(movement.x) || 0,
    y: Number(movement.y) || 0
  }
}

function resolvePreferredHorizontal (anchor, viewport, movement) {
  if (movement.x > 0) {
    return 'left'
  }
  if (movement.x < 0) {
    return 'right'
  }
  return anchor.x <= viewport.width / 2 ? 'right' : 'left'
}

function resolvePreferredVertical (anchor, viewport, movement, tooltipSize = {}, gap = 12, edgePadding = 12) {
  if (movement.y > 0) {
    return 'top'
  }
  if (movement.y < 0) {
    return 'bottom'
  }

  const tooltipHeight = Number(tooltipSize.height) || 0
  const requiredSpace = tooltipHeight + gap
  const availableTop = anchor.y - edgePadding
  const availableBottom = viewport.height - anchor.y - edgePadding
  const canPlaceTop = availableTop >= requiredSpace
  const canPlaceBottom = availableBottom >= requiredSpace

  if (canPlaceTop && !canPlaceBottom) {
    return 'top'
  }
  if (canPlaceBottom && !canPlaceTop) {
    return 'bottom'
  }
  if (canPlaceTop) {
    return 'top'
  }
  return availableBottom >= availableTop ? 'bottom' : 'top'
}

function buildCandidate (placement, anchor, tooltipSize, gap) {
  const { width, height } = tooltipSize
  const [vertical, horizontal] = placement.split('-')

  return {
    left: horizontal === 'left'
      ? anchor.x - width - gap
      : anchor.x + gap,
    top: vertical === 'top'
      ? anchor.y - height - gap
      : anchor.y + gap
  }
}

function isCandidateFullyVisible (candidate, tooltipSize, viewport, edgePadding) {
  return candidate.left >= edgePadding &&
    candidate.top >= edgePadding &&
    candidate.left + tooltipSize.width <= viewport.width - edgePadding &&
    candidate.top + tooltipSize.height <= viewport.height - edgePadding
}

function getPlacementOrder (anchor, viewport, movement, tooltipSize = {}, gap = 12, edgePadding = 12) {
  const preferredHorizontal = resolvePreferredHorizontal(anchor, viewport, movement)
  const preferredVertical = resolvePreferredVertical(anchor, viewport, movement, tooltipSize, gap, edgePadding)
  const alternateHorizontal = preferredHorizontal === 'left' ? 'right' : 'left'
  const alternateVertical = preferredVertical === 'top' ? 'bottom' : 'top'

  return [
    `${preferredVertical}-${preferredHorizontal}`,
    `${alternateVertical}-${preferredHorizontal}`,
    `${preferredVertical}-${alternateHorizontal}`,
    `${alternateVertical}-${alternateHorizontal}`
  ]
}

function resolveTooltipLayout ({ anchor, tooltipSize, viewport, movement, gap = 12, edgePadding = 12 } = {}) {
  if (!anchor) {
    return { left: 0, top: 0, placement: 'bottom-right' }
  }

  const normalizedViewport = normalizeViewport(viewport)
  const normalizedSize = normalizeSize(tooltipSize)
  const normalizedMovement = normalizeMovement(movement)
  const placements = getPlacementOrder(anchor, normalizedViewport, normalizedMovement, normalizedSize, gap, edgePadding)
  const preferredPlacement = placements[0] || 'bottom-right'
  const preferredCandidate = buildCandidate(preferredPlacement, anchor, normalizedSize, gap)

  if (isCandidateFullyVisible(preferredCandidate, normalizedSize, normalizedViewport, edgePadding)) {
    return {
      left: preferredCandidate.left,
      top: preferredCandidate.top,
      placement: preferredPlacement
    }
  }

  const maxLeft = normalizedViewport.width - normalizedSize.width - edgePadding
  const maxTop = normalizedViewport.height - normalizedSize.height - edgePadding

  return {
    left: clamp(preferredCandidate.left, edgePadding, maxLeft),
    top: clamp(preferredCandidate.top, edgePadding, maxTop),
    placement: preferredPlacement
  }
}

function isPointInExpandedRect (point, rect, padding = 16) {
  if (!point || !rect) {
    return false
  }

  return point.x >= rect.left - padding &&
    point.x <= rect.right + padding &&
    point.y >= rect.top - padding &&
    point.y <= rect.bottom + padding
}

function isPointerInTooltipTransferZone ({ pointer, anchor, tooltipRect, padding = 16 } = {}) {
  if (!pointer || !anchor || !tooltipRect) {
    return false
  }

  const bridgeRect = {
    left: Math.min(anchor.x, tooltipRect.left),
    top: Math.min(anchor.y, tooltipRect.top),
    right: Math.max(anchor.x, tooltipRect.right),
    bottom: Math.max(anchor.y, tooltipRect.bottom)
  }

  return isPointInExpandedRect(pointer, bridgeRect, padding)
}

module.exports = {
  resolveTooltipLayout,
  isPointerInTooltipTransferZone
}
